import { LocationDescriptor as To } from "history";
import { parse } from "query-string";
import React, { useCallback } from "react";
import { useSelector } from "react-redux";
import { LinkProps, useHistory } from "react-router-dom";

import { loginUrl, signupUrl } from "@/mk2/apps/users/urls";
import { isPWARoute } from "@/mk2/helpers/router";
import { AppState, getRequestUser } from "@/mk2/reducers";
import { UserEntity } from "@/mk2/schemas";
import { HistoryLocationState, RouteNavigateFunction } from "@/mk2/services/browserHistory";
import { apiAuth } from "@/mk2/services/browserXhr";
import {
    createPathBackport,
    parsePathBackport,
    useNavigateBackport,
} from "@/mk2/services/historyBackports";
import { CURRENT_REVISION } from "@/mk/autogenerated/gitData";
import { hasAds } from "@/mk/bazaar/common/userUtils";
import { BASE_URL, IS_PROD } from "@/mk/settings";

export type OwnProps =
    // inherit from react-router-dom/LinkProps
    LinkProps & {
        to: To | null;
        state?: Partial<HistoryLocationState>;
    };

interface StateProps {
    release: string;
    requestUser: UserEntity;
}

export const Link = React.memo(
    React.forwardRef<HTMLAnchorElement, OwnProps>((ownProps, ref) => {
        const { to, state, replace = false, target, onClick, ...rest } = ownProps;
        const history = useHistory<HistoryLocationState>();
        const routeNavigate: RouteNavigateFunction = useNavigateBackport(history);

        const stateProps = useSelector((state: AppState) => mapStateToProps(state, ownProps));
        const { release, requestUser } = stateProps;

        const onClickCb = useCallback(
            (event: React.MouseEvent<HTMLAnchorElement>) => {
                const dest: To = !to
                    ? { pathname: "" }
                    : typeof to === "string"
                      ? parsePathBackport(to)
                      : to;

                // Remove BASE_URL from absolute links
                if (dest.pathname?.startsWith(BASE_URL)) {
                    dest.pathname = dest.pathname.substring(BASE_URL.length);
                }

                if (onClick) {
                    onClick(event);
                }

                if (
                    !event.defaultPrevented && // onClick prevent default
                    event.button === 0 && // ignore everything but left clicks
                    !target && // let browser handle "target=_blank" etc.
                    !isModifiedEvent(event) && // ignore clicks with modifier keys
                    isPWARoute(dest.pathname) // Check if we have handler for this URL
                ) {
                    event.preventDefault();

                    if (IS_PROD && !!release && release !== CURRENT_REVISION) {
                        window.location.href = dest.pathname;
                    } else {
                        try {
                            apiAuth.verifier(); // Throws exception if csrf token has changed
                            routeNavigate(dest, {
                                replace,
                                state: { ...dest.state, ...state },
                            });
                        } catch (e) {
                            window.location.href = dest.pathname;
                        }
                    }
                }
            },
            [to, onClick, target, release, state, replace, routeNavigate],
        );

        let href: string = !to ? "" : typeof to == "string" ? to : createPathBackport(to);
        // Remove BASE_URL from absolute links
        if (href.startsWith(BASE_URL)) {
            href = href.substring(BASE_URL.length);
        }

        // https://trello.com/c/tamkDLUC/6540-do-get-parametrov-pre-login-a-signup-linky-v-headeri-nevkladat-nexturl-ale-pridat-az-na-onclick
        // Ak URL obsahuje query string, skontroluj parameter `next`.
        // Ak ide o login alebo signup URL, odstráni sa query časť.
        const hrefStartsWithSignupOrLogin = href.startsWith(loginUrl) || href.startsWith(signupUrl);
        if (href.includes("?")) {
            const qs = parse(href.substr(href.indexOf("?")));
            if (qs.next && hrefStartsWithSignupOrLogin) {
                href = href.startsWith(loginUrl) ? loginUrl : signupUrl;
            }
        }

        // Určí, či sa má zobraziť interstitial reklama od Googlu.
        // 1. Ak URL smeruje na login alebo signup, reklama sa nezobrazí ('false').
        // 2. Inak sa použije hodnota z atribútu `data-google-interstitial`.
        // 3. Ak chýba, použije sa `requestUserHasAds` (či má používateľ povolené reklamy).
        // 4. Ak nič z toho nie je dostupné, výsledok bude 'false'.
        const dataGoogleInterstitial = hrefStartsWithSignupOrLogin
            ? "false"
            : ((rest["data-google-interstitial"] || hasAds(requestUser)) ?? "false");

        return (
            <a
                {...rest}
                onClick={onClickCb}
                href={href}
                ref={ref}
                data-google-interstitial={dataGoogleInterstitial}
            />
        );
    }),
);

function mapStateToProps(state: AppState, ownProps: OwnProps): StateProps {
    return {
        requestUser: getRequestUser(state),
        release: state.release?.version,
    };
}

function isModifiedEvent(event: React.MouseEvent<HTMLAnchorElement>) {
    return !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
}
